import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import Helmet from 'react-helmet';
import CabModifySearch from '../../components/CabModifySearch/CabModifySearch';
import FaqSection from '../../components/Faq/Faq';
import Footer from '../../components/Footer/Footer';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import { HR_STRING } from '../../constants/miscellaneousConstants';
import AppContextProvider from '../../AppContext';
import CompactList from '../../components/CompactList/CompactList';
import capitalise from '../../utils/capitalise';
import { trackCityHRTemplateEvent } from '../../utils/omnitureTracking';
import '../CityTemplate/CityTemplate.css';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import Overlay from '../../components/BPG/Overlay';
import { useKnowMoreDetails } from '../customHooks';
import {
  fetchAndLogXdmEvent,
  useSearchWidgetHooks
} from '../../utils/commonUtils';
import AdsComponent from '../../components/adsComponent';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import RentalCabs from '../../components/RentalCabs/RentalCabs';
import '../../pages/RouteCarTypeTemplate/RouteCarTypeTemplate.css';
import '../CityHRTemplate/CityHRTemplate.css';

import HourlyRentalsCabs from '../../components/HourlyRentalsCabs/HourlyRentalsCabs';
import { RouteComponentProps } from 'react-router';
import {
  CityHRTemplateChecker,
  CityHRTemplateData
} from './CityHRTemplateDataChecker';
import { PathReporter } from 'io-ts/lib/PathReporter';
import { SEO_CITY_HR_TEMPLATE } from '../../constants/templateConstants';
import { yeildToMainThread } from '../../scheduler';
import ProductSchema from '../../utils/ProductSchema';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const isSSR = typeof window === 'undefined';

type PathParamsType = {
  cityHRId: string;
};
type CityHRTemplateProp = RouteComponentProps<PathParamsType> & {
  data: CityHRTemplateData;
};

const runtimeTypChecker = (data: CityHRTemplateData) => {
  const result = CityHRTemplateChecker.decode(data);
  if (result._tag !== 'Right') {
    console.log(PathReporter.report(result));
    //  ThrowReporter.report(result);
  }
};

const CityHRTemplate = ({ data }: CityHRTemplateProp) => {
  runtimeTypChecker(data);
  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)'
  });
  const [
    selectedKnowMoreDetailsForMobile,
    setSelectedKnowMoreDetailsForMobile
  ] = useKnowMoreDetails(null, isMobileDevice);
  const delayedKnowMoreDetailsForMobile = async val => {
    await yeildToMainThread(() => setSelectedKnowMoreDetailsForMobile(val));
  };

  const {
    cityData,
    templateData,
    descriptionInHtml: cityDescriptionInHTML,
    description,
    hrPackages,
    interlinks,
    aggregateRating,
    schemaMinFare
  } = data;
  const {
    name: cityName,
    cabModelsAvailableForRent,
    cabTypesAvailableForRent,
    code,
    name
  } = cityData;
  const HRCapitalized = capitalise(HR_STRING);
  const { travelFor, pickUpTime, dropTime } = useSearchWidgetHooks(
    SEO_CITY_HR_TEMPLATE
  );
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const toggleOverlay = (isVisible: boolean) => {
    setIsOverlayVisible(isVisible);
  };

  useEffect(() => {
    const url = typeof window !== 'undefined' ? window.location.href : data.url;
    fetchAndLogXdmEvent(url);
  }, []);

  return (
    <AppContextProvider
      omniUrlArgs={`source=${SEO_CITY_HR_TEMPLATE}`}
      trackTemplateEvent={trackCityHRTemplateEvent}
      selectedKnowMoreDetailsForMobile={selectedKnowMoreDetailsForMobile}
      setSelectedKnowMoreDetailsForMobile={delayedKnowMoreDetailsForMobile}
    >
      <div
        className={`cabSeoDtPage ${
          selectedKnowMoreDetailsForMobile ? 'expanded' : ''
        }`}
      >
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
          {templateData.ogTitle && (
            <meta property="og:title" content={templateData.ogTitle} />
          )}
          {templateData.ogDescription && (
            <meta
              property="og:description"
              content={templateData.ogDescription}
            />
          )}
          {templateData.ogImage && (
            <meta name="og:image" content={templateData.ogImage} />
          )}
          {templateData.twitterTitle && (
            <meta name="twitter:title" content={templateData.twitterTitle} />
          )}
          {templateData.twitterDescription && (
            <meta
              name="twitter:description"
              content={templateData.twitterDescription}
            />
          )}
          {templateData.twitterImage && (
            <meta name="twitter:image" content={templateData.twitterImage} />
          )}
        </Helmet>
        {aggregateRating && (
          <ProductSchema
            data={aggregateRating}
            templateData={templateData}
            schemaMinFare={schemaMinFare}
            source={name}
          />
        )}
        <div className={`desktopShow makeSticky`}>
          <CabModifySearch
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            isMobileDevice={false}
            title={`${cityName} Cabs`}
            hrPackages={hrPackages}
            sourceCityCode={code}
            sourceCityName={name}
          />
        </div>
        <div className={`domChanger mobileShow makeSticky`}>
          <CabModifySearch
            isOverlayVisible={isOverlayVisible}
            overlayToggle={toggleOverlay}
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            isMobileDevice={true}
            title={`${cityName} Cabs`}
            hrPackages={hrPackages}
            sourceCityCode={code}
            sourceCityName={name}
          />
        </div>
        <main className="container">
          <BreadCrumb breadcrumb={templateData.breadcrumb} />
          <PageHeader
            title={templateData.h1Title.replace(HR_STRING, HRCapitalized)}
            extraInfo={templateData.extraInfo}
            isMobileDevice={isMobileDevice}
          />
          {/* <div className="appendBottom40">
            {
              <AdsComponent
                template={'City_HR'}
                placedAt={'top'}
                isMobileDevice={isMobileDevice}
                desktopAdConfig={{
                  position: templateData.bannerDesktop
                    ? templateData.bannerDesktop
                    : 'none',
                  class: 'desktopShow appendBottom20 adsLayout',
                  style: {}
                }}
                mobileAdConfig={{
                  position: templateData.bannerMobile
                    ? templateData.bannerMobile
                    : 'none',
                  class: 'mobileShow appendBottom20 adsLayout',
                  style: { marginTop: null }
                }}
              />
            }
          </div> */}
          {cabModelsAvailableForRent &&
            cabModelsAvailableForRent.packages &&
            cabModelsAvailableForRent.packages.length > 0 && (
              <HourlyRentalsCabs
                cityName={cityName}
                cabsAvailableForRent={cabModelsAvailableForRent}
                h2String={`Rentals Cabs in ${cityName}`}
              />
            )}
          {cabTypesAvailableForRent &&
            cabTypesAvailableForRent.packages &&
            cabTypesAvailableForRent.packages.length > 0 && (
              <>
                <RentalCabs
                  h2={`Rental Car In ${cityName}`}
                  cityName={cityName}
                  data={cabTypesAvailableForRent}
                />
                <br />
              </>
            )}
          <div
            style={{ flexWrap: 'wrap' }}
            className={`cabsSeoWrap__Details makeFlex visibilityAuto`}
          >
            {interlinks &&
              interlinks.map(({ title, links }) => {
                return (
                  <CompactList
                    titlePrefix={title}
                    titleSuffix={``}
                    list={links}
                    type={'bullets'}
                    className={'rightMargin'}
                  >
                    {(item: any, idx, style) => (
                      <li
                        key={`${item.text}_${idx}`}
                        className={`${
                          idx ? `paddingT10 paddingB10` : `paddingB10`
                        }`}
                      >
                        {item.url ? (
                          <a
                            className={`linkBoxFont `}
                            onClick={() => {
                              //TO DO: add omniture events from Dataloader
                            }}
                            href={item.url}
                          >
                            {item.text}
                          </a>
                        ) : (
                          <span>{item.text}</span>
                        )}
                      </li>
                    )}
                  </CompactList>
                );
              })}
          </div>
          {templateData.descriptionInHtml ? (
            <section id="descriptionInHtml" className="visibilityAuto">
              <ThingsToKnow html={templateData.descriptionInHtml} />
            </section>
          ) : null}
          {cityDescriptionInHTML ? (
            <section id="descriptionInHtml" className="visibilityAuto">
              <ThingsToKnow html={cityDescriptionInHTML} />
            </section>
          ) : null}
          {description ? (
            <section className="visibilityAuto">
              <h2>{`Things To Know About ${cityName}`}</h2>
              <ThingsToKnow text1={description} />
            </section>
          ) : null}
          <section className="visibilityAuto">
            <h2>{`Frequently Asked Questions About ${cityName} ${
              HR_STRING ? HRCapitalized + ' ' : ''
            }Booking`}</h2>
            <FaqSection
              trackTemplateEvent={trackCityHRTemplateEvent}
              data={templateData.faq}
              pageUrl={templateData.sampleUrl}
            />
          </section>
        </main>
        {selectedKnowMoreDetailsForMobile ? (
          <Overlay
            cancelButton={() => delayedKnowMoreDetailsForMobile(null)}
            knowMoreDetails={selectedKnowMoreDetailsForMobile}
          />
        ) : null}
        <Footer template={templateData} />
        <div
          className="blackOverlay"
          onClick={() => {
            delayedKnowMoreDetailsForMobile(null);
          }}
        ></div>
        {isOverlayVisible && (
          <div
            onClick={() => {
              toggleOverlay(false);
            }}
            className="searchWidgetModal mobileShow"
          ></div>
        )}
      </div>
    </AppContextProvider>
  );
};

CityHRTemplate.fetchData = async (_, match, urlObj) => {
  const {
    params: { cityHRId }
  } = match;
  const url = `${DATA_LOADER_URL}/api/cityHRTemplateData/${cityHRId}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();

  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception:- ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch city details of ${cityHRId}`,
      responseData.error
    );
  }
  return responseData;
};
export default CityHRTemplate;
